import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { GmailScope, UserIntegration } from "schemas/dashboard";
import { Map } from "schemas/functions";

import GoogleConnect from "features/Settings/GoogleConnect";
import { isCommonDomain } from "utils/emails";

interface IProps {
  openIntegrationDialog: boolean;
  setOpenIntegrationDialog: (newOpen: boolean) => void;
  emailHealth: boolean;
  userIntegrations: UserIntegration[];
  userIntegration: UserIntegration | null;
  scopes: Map | null;
  onSuccessAction?: () => Promise<void>;
  integrationDialogText?: string;
  title?: string;
}

export default function ConnectGmailDialog({
  openIntegrationDialog,
  setOpenIntegrationDialog,
  emailHealth,
  userIntegrations,
  userIntegration,
  scopes,
  onSuccessAction,
  integrationDialogText,
  title = "Connect your Gmail account to send emails",
}: IProps) {
  const renderDialogContent = () => {
    if (userIntegrations?.length === 0) {
      return (
        <>
          <Typography>
            You must allow access so we can send emails and schedule followup
            emails on your behalf ✨ You can revoke your access at any time.
          </Typography>
          <Box sx={{ mt: 1 }} display="flex" justifyContent="center">
            <GoogleConnect onSuccessAction={onSuccessAction} />
          </Box>
        </>
      );
    } else if (
      userIntegrations &&
      userIntegrations.length >= 1 &&
      scopes !== null &&
      (!scopes[GmailScope.SEND] || !scopes[GmailScope.READ])
    ) {
      return (
        <>
          <Typography
            gutterBottom
            sx={{ color: "warning.main" }}
            variant="body2"
          >
            Insufficient permissions granted to Bento
          </Typography>
          In order for you to send emails you must provide additional access.
          Sign in with Google and select: <br />
          <ul>
            {!scopes[GmailScope.SEND] && (
              <li>
                <Typography>Send Email on your behalf.</Typography>
              </li>
            )}
            {!scopes[GmailScope.READ] && (
              <li>
                <Typography>
                  View your email messages and settings. We use this to
                  accurately schedule follow ups.
                </Typography>
              </li>
            )}
          </ul>
          <Box sx={{ mt: 1 }} display="flex" justifyContent="center">
            <GoogleConnect onSuccessAction={onSuccessAction} />
          </Box>
        </>
      );
    } else if (userIntegration?.integrationError && userIntegration?.email) {
      return (
        <>
          {isCommonDomain(userIntegration?.email) ? (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>
                You are currently attempting to connect Bento with a{" "}
                <strong>non-gmail account.</strong> You need to connect to an
                inbox that runs on Gmail to send emails with Bento.
              </Alert>
              <Typography>
                Please create an @gmail.com account and try connecting again.
                See{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://support.google.com/accounts/answer/27441?hl=en&co=GENIE.Platform%3DDesktop&oco=0"
                >
                  this link
                </a>{" "}
                for how to create a Google account.
              </Typography>
              <Typography paragraph>
                If you have any questions, you can reach out to{" "}
                <a href="mailto:hello@onbento.com">hello@onbento.com</a> for
                assistance.
              </Typography>
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>
                You are currently attempting to connect Bento with a Google
                account that does not have Gmail enabled. You need to connect to
                an inbox that runs on Gmail to send emails with Bento.
              </Alert>
              <Typography paragraph>
                If you are using Google Workspace, your domain must be{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://support.google.com/a/answer/60216?hl=en"
                >
                  verified with Google Workspace
                </a>{" "}
                and{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://support.google.com/a/answer/172171?hl=en"
                >
                  Gmail must be activated
                </a>
                .
              </Typography>
              <Typography paragraph>
                Please make these changes or use an @gmail.com account and try
                connecting again. If you have any questions, you can reach out
                to <a href="mailto:hello@onbento.com">hello@onbento.com</a> for
                assistance.
              </Typography>
            </>
          )}
          <Box display="flex" sx={{ mb: 2 }} justifyContent="center">
            <GoogleConnect onSuccessAction={onSuccessAction} />
          </Box>
        </>
      );
    } else {
      return (
        <>
          <Typography>
            You must re-connect to Gmail so we can send emails and schedule
            followup emails on your behalf ✨ You can revoke your access at any
            time.
          </Typography>
          <Box sx={{ mt: 1 }} display="flex" justifyContent="center">
            <GoogleConnect onSuccessAction={onSuccessAction} />
          </Box>
        </>
      );
    }
  };

  return (
    <Dialog
      maxWidth="sm"
      open={openIntegrationDialog}
      onClose={() => setOpenIntegrationDialog(false)}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        {emailHealth ? (
          <>
            <Typography>Success! You are connected.</Typography>
          </>
        ) : (
          <>
            {!!integrationDialogText ? (
              <>
                <Typography>{integrationDialogText}</Typography>
                <Box sx={{ mt: 1 }} display="flex" justifyContent="center">
                  <GoogleConnect onSuccessAction={onSuccessAction} />
                </Box>
              </>
            ) : (
              renderDialogContent()
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!emailHealth ? (
          <Button
            color="secondary"
            onClick={() => setOpenIntegrationDialog(false)}
          >
            Close
          </Button>
        ) : (
          <Button onClick={() => setOpenIntegrationDialog(false)}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
